import React from 'react'
import classNames from 'classnames'

/**
 * One outcome of a list row as a line of text: the label, and the probability at the
 * right edge, where the eye can run down the column and compare one market against the
 * next without reading a label. The market row and the fixture row both draw their
 * outcomes with it, so the two lists read as one table.
 *
 * Text, not a button, but still the bet control: the line selects its outcome and aims
 * the bet pad. Under the pointer it says so with a wash; once picked, the whole line goes
 * bold in the brand colour — the one place in the list the brand appears, so it can only
 * mean "this is what the pad is aimed at".
 */
const OutcomeLine = ({
  label,
  percentage,
  winner = false,
  selected = false,
  clickable = false,
  /** Whether the line has lost: a resolved market fades everything but its winner. */
  faded = false,
  onClick,
  /** The hover-hint handlers from `useTruncatedLabelTooltip`, spread on the line. */
  hintProps,
  /** Drawn before the label, such as a fixture side's crest. */
  leading = null,
}) => (
  <div
    role={clickable ? 'button' : undefined}
    tabIndex={clickable ? 0 : undefined}
    aria-pressed={clickable ? selected : undefined}
    {...hintProps}
    onClick={
      clickable
        ? event => {
            event.stopPropagation()
            onClick?.()
          }
        : undefined
    }
    onKeyDown={
      clickable
        ? event => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            event.stopPropagation()
            onClick?.()
          }
        : undefined
    }
    className={classNames(
      /* The wash bleeds 6px past the text on each side and the negative margin takes it
         back, so the label stays on the column's edge whether or not it is hovered. */
      /* 32px tall, so the wash under the pointer reads as a target of its own. */
      'group -mx-1.5 flex h-8 min-w-0 items-center justify-between gap-3 rounded-[var(--radius-xs)] px-1.5 text-sm leading-none',
      'outline-none transition-colors duration-150 focus-visible:ring-1 focus-visible:ring-brand-4',
      selected ? 'font-bold text-brand-4' : 'text-Neutral-5',
      clickable && 'cursor-pointer hover:bg-surface-3',
      clickable && !selected && 'hover:text-brand-4',
      { 'cursor-not-allowed': !clickable, 'opacity-50': !clickable || faded }
    )}>
    <span className="flex min-w-0 items-center gap-2">
      {leading}
      <span className="min-w-0 truncate" data-truncatable-label>
        {label}
      </span>
    </span>
    {/* The figure holds the right edge. On a resolved market the winner carries the
        trophy there instead. */}
    {winner ? (
      <span className="shrink-0" aria-hidden="true">
        🏆
      </span>
    ) : (
      <span
        className={classNames(
          'shrink-0 tabular-nums',
          selected ? 'font-bold' : 'font-semibold text-Neutral-6',
          clickable && !selected && 'group-hover:text-brand-4'
        )}>
        {percentage}%
      </span>
    )}
  </div>
)

export default OutcomeLine
